import { useIdentity } from "base/identity-resolver";
import { useState, useEffect } from "react";
import { setTimestampToMidnightAndRemoveMilliseconds } from "../../utils/helpers";
import {
  getJourneyTimeoutsForUser,
  createJourneyTimeout,
  updateJourneyTimeout,
  cancelJourneyTimeout,
} from "../../utils/api-requests/hooks/use-journey-timeouts-requests";

// Custom Hook to handle all Cadence Pausing/Journey Timeout API Requests
const useJourneyTimeoutsApi = () => {
  const { user, org } = useIdentity();
  const [inProgressJourneyTimeout, setInProgressJourneyTimeout] = useState(null);
  const [futureJourneyTimeout, setFutureJourneyTimeout] = useState(null);
  const [refetchJourneyTimeout, setRefetchJourneyTimeout] = useState(false);
  const [businessDaysUntilFutureTimeout, setBusinessDaysUntilFutureTimeout] = useState(0);

  // Populate state
  useEffect(() => {
    const getAndSetUserTimeouts = async () => {
      const { activeJourneyTimeout = null, scheduledJourneyTimeouts = [] } = await getJourneyTimeoutsForUser(user.id);

      setInProgressJourneyTimeout(activeJourneyTimeout);
      setFutureJourneyTimeout(null);

      if (!activeJourneyTimeout && scheduledJourneyTimeouts.length) {
        // only allow user to view a future pause if there is no active pause
        const sortedScheduledTimeouts = scheduledJourneyTimeouts.sort((a, b) => a.pause_start - b.pause_start); // this should happen on the backend but doesn't currently
        setFutureJourneyTimeout(sortedScheduledTimeouts[0]);
      }

      setRefetchJourneyTimeout(false);
    };

    getAndSetUserTimeouts();
  }, [user.id, refetchJourneyTimeout]);

  // Repopulate State after arbitrary delay
  const refetchUserTimeoutAfterDelay = (setShowSettings = () => {}) => {
    setTimeout(() => {
      setRefetchJourneyTimeout(true); // fetch user timeout again
      setShowSettings(false); // navigate away from settings page, back to main page
    }, 1000);
  };

  // API Calls
  const handleCreate = (start, end, setShowSettings = () => {}) => {
    createJourneyTimeout(buildPayload(start, end));
    refetchUserTimeoutAfterDelay(setShowSettings);
  };

  const handleUpdate = (start, end, setShowSettings = () => {}) => {
    updateJourneyTimeout(
      buildPayload(start, end),
      inProgressJourneyTimeout ? inProgressJourneyTimeout.id : futureJourneyTimeout.id
    );
    refetchUserTimeoutAfterDelay(setShowSettings);
  };

  const handleCancel = (setShowSettings = () => {}) => {
    // should only be called for FUTURE timeout
    cancelJourneyTimeout(futureJourneyTimeout.id);
    refetchUserTimeoutAfterDelay(setShowSettings);
  };

  const handleResume = (setShowSettings = () => {}) => {
    const payload = {
      ...inProgressJourneyTimeout,
      pause_end: setTimestampToMidnightAndRemoveMilliseconds(new Date()), // pause_end: midnight today
    };

    updateJourneyTimeout(payload, inProgressJourneyTimeout.id);
    refetchUserTimeoutAfterDelay(setShowSettings);
  };

  // Helper function to build API Payload
  function buildPayload(startDate, endDate) {
    return {
      owner_user_id: user.id,
      oid: org.id,
      pause_start: setTimestampToMidnightAndRemoveMilliseconds(startDate),
      pause_end: setTimestampToMidnightAndRemoveMilliseconds(endDate),
    };
  }

  useEffect(() => {
    if (futureJourneyTimeout) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const pauseStart = new Date(futureJourneyTimeout.pause_start * 1000);
      pauseStart.setHours(0, 0, 0, 0);

      let totalBusinessDays = 0;

      for (let current = new Date(today); current <= pauseStart; current.setDate(current.getDate() + 1)) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          totalBusinessDays++;
        }
      }
      setBusinessDaysUntilFutureTimeout(totalBusinessDays);
    }
  }, [futureJourneyTimeout]);

  return {
    inProgressJourneyTimeout,
    futureJourneyTimeout,
    handleCreate,
    handleUpdate,
    handleCancel,
    handleResume,
    businessDaysUntilFutureTimeout,
  };
};

export default useJourneyTimeoutsApi;
